import { useCallback, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { queryKeys } from "api/queryKeys"
import { notify } from "shared/ui/Notifications/notify"
import {
  checkQuestionnaire,
  getQuestionnaire,
  saveQuestionnaire,
  getQuestionnaireTranslations,
  saveQuestionnaireTranslations,
} from "api/Repos/questionnaire"
import {
  QuestionnaireGetResponse,
  QuestionnaireSaveOptions,
  QuestionnaireSaveResponse,
  QuestionnaireSaveResult,
  QuestionnaireCheckResponse,
  QuestionnaireTranslationsGetResponse,
  QuestionnaireTranslationsSaveResponse,
  Translation,
} from "types/questionnaire.types"

/**
 * Questionnaire document operations. Imperative by design: the editor owns
 * the document state (with Yjs collaboration on top), so there is no query
 * cache — reads are plain authed calls, saves run as react-query mutations.
 */
interface UseQuestionnaireReturn {
  getQuestionnaireByID: (
    id: string,
    owner: string
  ) => Promise<QuestionnaireGetResponse>
  isLoadingQuestionnaire: boolean

  /** Background fetch (autosave flows) — no loading state. */
  getQuestionnaireSilent: (
    id: string,
    owner: string
  ) => Promise<QuestionnaireGetResponse>

  /** Saves the questionnaire; `options.checkpoint` marks a save that cuts a version. */
  saveQuestionnaireByID: (
    id: string,
    qnr: string,
    owner: string,
    options?: QuestionnaireSaveOptions
  ) => Promise<QuestionnaireSaveResponse>
  isSavingQuestionnaire: boolean

  checkQuestionnaireByID: (id: string) => Promise<QuestionnaireCheckResponse>
  isCheckingQuestionnaire: boolean

  getQuestionnaireTranslationsByID: (
    id: string,
    owner: string
  ) => Promise<QuestionnaireTranslationsGetResponse>
  isGettingQuestionnaireTranslations: boolean

  saveQuestionnaireTranslationsByID: (
    id: string,
    owner: string,
    translations: Translation[]
  ) => Promise<QuestionnaireTranslationsSaveResponse>
  isSavingQuestionnaireTranslations: boolean
}

// One questionnaire save: the project, the text, its owner, and whether it is a checkpoint.
interface SaveArgs {
  id: string
  qnr: string
  owner: string
  checkpoint: boolean
}

/**
 * What to tell the user after a checkpoint save, or null when there is nothing to say (a new
 * version was recorded, or nothing changed since the last one).
 */
export const checkpointNotice = (reply: QuestionnaireSaveResult | undefined): string | null => {
  if (reply?.auditStatus === "not_recorded") {
    if (reply.reason === "duplicate_label")
      return `Saved. No version was added to the history: two questions share the label "${reply.label ?? ""}".`
    if (reply.reason === "empty")
      return "Saved. No version was added to the history: the questionnaire is empty."
    return "Saved. No version was added to the history."
  }
  if (reply?.auditStatus === "skipped")
    return "Saved. There are no questions yet, so no version was added to the history."
  return null
}

export const useQuestionnaire = (): UseQuestionnaireReturn => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  const [isLoadingQuestionnaire, setIsLoadingQuestionnaire] = useState(false)
  const [isCheckingQuestionnaire, setIsCheckingQuestionnaire] = useState(false)
  const [
    isGettingQuestionnaireTranslations,
    setIsGettingQuestionnaireTranslations,
  ] = useState(false)

  const getQuestionnaireSilent = useCallback(
    async (id: string, owner: string): Promise<QuestionnaireGetResponse> =>
      getQuestionnaire(await getAxios(), id, owner),
    [getAxios]
  )

  const getQuestionnaireByID = useCallback(
    async (id: string, owner: string): Promise<QuestionnaireGetResponse> => {
      setIsLoadingQuestionnaire(true)
      try {
        return await getQuestionnaireSilent(id, owner)
      } finally {
        setIsLoadingQuestionnaire(false)
      }
    },
    [getQuestionnaireSilent]
  )

  const checkQuestionnaireByID = useCallback(
    async (id: string): Promise<QuestionnaireCheckResponse> => {
      setIsCheckingQuestionnaire(true)
      try {
        return await checkQuestionnaire(await getAxios(), id)
      } finally {
        setIsCheckingQuestionnaire(false)
      }
    },
    [getAxios]
  )

  const getQuestionnaireTranslationsByID = useCallback(
    async (
      id: string,
      owner: string
    ): Promise<QuestionnaireTranslationsGetResponse> => {
      setIsGettingQuestionnaireTranslations(true)
      try {
        return await getQuestionnaireTranslations(await getAxios(), id, owner)
      } finally {
        setIsGettingQuestionnaireTranslations(false)
      }
    },
    [getAxios]
  )

  const saveMutation = useMutation({
    mutationFn: async ({ id, qnr, owner, checkpoint }: SaveArgs) =>
      saveQuestionnaire(await getAxios(), id, qnr, owner, { checkpoint }),
    // A checkpoint save cuts a version: say so when it couldn't (the questionnaire is saved
    // either way), and refresh the cached provenance reads (Timeline, verify, version ID, drift)
    // so they show the new version. An autosave does neither: it records no version, and
    // refreshing every 10 s would re-read and re-check the history.
    onSuccess: (res, { id, checkpoint }) => {
      if (!checkpoint) return
      const notice = checkpointNotice(res.data)
      if (notice) notify.toast.warning(notice)
      void queryClient.invalidateQueries({
        queryKey: queryKeys.provenance.project(id),
      })
    },
  })

  const saveTranslationsMutation = useMutation({
    mutationFn: async (args: [string, string, Translation[]]) =>
      saveQuestionnaireTranslations(await getAxios(), ...args),
  })

  const saveQuestionnaireByID = useCallback(
    (id: string, qnr: string, owner: string, options: QuestionnaireSaveOptions = {}) =>
      saveMutation.mutateAsync({ id, qnr, owner, checkpoint: !!options.checkpoint }),
    [saveMutation.mutateAsync]
  )

  const saveQuestionnaireTranslationsByID = useCallback(
    (id: string, owner: string, translations: Translation[]) =>
      saveTranslationsMutation.mutateAsync([id, owner, translations]),
    [saveTranslationsMutation.mutateAsync]
  )

  return {
    getQuestionnaireByID,
    isLoadingQuestionnaire,
    getQuestionnaireSilent,
    saveQuestionnaireByID,
    isSavingQuestionnaire: saveMutation.isPending,
    checkQuestionnaireByID,
    isCheckingQuestionnaire,
    getQuestionnaireTranslationsByID,
    isGettingQuestionnaireTranslations,
    saveQuestionnaireTranslationsByID,
    isSavingQuestionnaireTranslations: saveTranslationsMutation.isPending,
  }
}
